import { http } from "shared/api/http";
import { IProject, IProjectsInfo } from "projects/models";
import { ISelectOption, PaginatedResult } from "shared/models";

const buildProjectsPath = (tenantId: string, organizationId: string) =>
  `${process.env.REACT_APP_API_BASE_URL}/api/v1/tenants/${tenantId}/organizations/${organizationId}/projects`;

export const getProjects = async (
  tenantId: string,
  organizationId: string,
  projectId?: string,
  status?: string,
  subcontractor?: string,
  utilityProgram?: string,
  accountManager?: string,
  auditor?: string,
  customerPaymentMethod?: string,
  stage?: string,
  showArchived?: boolean,
  lastEvaluatedKey?: any,
  limit?: number
) => {
  if (!tenantId || !organizationId) {
    throw new Error("tenantId and organizationId are required to fetch projects");
  }

  const params: Record<string, any> = {
    projectId,
    status,
    subcontractor,
    utilityProgram,
    accountManager,
    auditor,
    customerPaymentMethod,
    stage,
    showArchived,
    lastEvaluatedKey,
    limit,
  };

  Object.keys(params).forEach((key) => {
    if (params[key] === undefined) {
      delete params[key];
    }
  });

  const res = await http.get<PaginatedResult<IProject>>(
    buildProjectsPath(tenantId, organizationId),
    { params }
  );
  return res?.data;
};

export const getProject = async (tenantId: string, organizationId: string, id: string) => {
  const res = await http.get<IProject>(`${buildProjectsPath(tenantId, organizationId)}/${id}`);
  return res?.data;
};

export interface ProjectResult {
  project: IProject;
  warnings?: string[];
}

const encodeNoteContent = (project: IProject): IProject => {
  if (project.latestProjectNote?.content) {
    return {
      ...project,
      latestProjectNote: {
        ...project.latestProjectNote,
        content: btoa(encodeURIComponent(project.latestProjectNote.content)),
        contentEncoded: true,
      },
    };
  }
  return project;
};

export const addProject = async (tenantId: string, organizationId: string, project: IProject) => {
  const res = await http.post<ProjectResult>(buildProjectsPath(tenantId, organizationId), encodeNoteContent(project));
  return res?.data;
};

export const updateProject = async (tenantId: string, organizationId: string, id: string, project: IProject) => {
  const res = await http.put<ProjectResult>(
    `${buildProjectsPath(tenantId, organizationId)}/${id}`,
    encodeNoteContent(project)
  );
  return res?.data;
};

export const deleteProject = async (tenantId: string, organizationId: string, id: string) => {
  await http.delete(`${buildProjectsPath(tenantId, organizationId)}/${id}`);
};

export interface RecreateFolderResult {
  status: "created" | "failed" | "skipped";
  webUrl?: string | null;
  warning?: string;
}

export const recreateSharePointFolder = async (
  tenantId: string,
  organizationId: string,
  id: string
) => {
  const res = await http.post<RecreateFolderResult>(
    `${buildProjectsPath(tenantId, organizationId)}/${id}/sharepoint-folder`
  );
  return res?.data;
};

export const getUtilityProgramLabel = (utilityProgram: string, options?: ISelectOption[]) => {
  const label = options?.find((p) => p.value === utilityProgram)?.label;
  return label || utilityProgram;
};

export const getProjectsInfo = async (tenantId: string, organizationId: string) => {
  if (!tenantId || !organizationId) {
    throw new Error("tenantId and organizationId are required to fetch projects info");
  }

  const res = await http.get<IProjectsInfo>(
    `${buildProjectsPath(tenantId, organizationId)}/info`
  );
  return res?.data;
};

export const getProjectOptions = async (
  tenantId: string,
  organizationId: string,
  search: string
) => {
  if (!tenantId || !organizationId) {
    throw new Error("tenantId and organizationId are required to fetch project options");
  }

  const res = await http.get<ISelectOption[]>(
    `${buildProjectsPath(tenantId, organizationId)}/options`,
    { params: { search } }
  );
  return res?.data;
};
